import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Play, LogIn, UserPlus } from 'lucide-react';

const Login = () => {
  const { login, API } = useApp();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' or 'setup'
  const [loading, setLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: ''
  });

  // Check if setup is needed
  React.useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await axios.get(`${API}/admin/check-setup`);
        if (response.data.needs_admin) {
          setNeedsSetup(true);
          setMode('setup');
        }
      } catch (error) {
        console.error('Failed to check setup:', error);
      }
    };
    checkSetup();
  }, [API]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.username, formData.password);
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/setup/initial-admin`, {
        username: formData.username,
        password: formData.password,
        email: formData.email || null,
        role: 'admin'
      });
      toast.success('Admin account created! Please login.');
      setMode('login');
      setNeedsSetup(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-void flex items-center justify-center px-4">
      {/* Background Effects */}
      <div className="fixed inset-0 bg-glow-red opacity-20 pointer-events-none" />
      <div className="noise-overlay" />

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-brand-red mx-auto mb-4 flex items-center justify-center glow-red">
            <Play className="w-8 h-8 text-white fill-white" />
          </div>
          <h1 className="font-syne font-bold text-2xl text-white">GOLDENG</h1>
          <p className="text-white/50 text-sm mt-1">Admin Panel</p>
        </div>

        {/* Form Card */}
        <div className="bg-brand-dark rounded-lg p-8 border border-white/5" data-testid="login-form-container">
          <h2 className="font-syne font-semibold text-xl text-white mb-6 text-center">
            {mode === 'setup' ? 'Create Admin Account' : 'Sign In'}
          </h2>

          {needsSetup && mode === 'login' && (
            <div className="mb-6 p-4 bg-brand-gold/10 border border-brand-gold/30 rounded-lg">
              <p className="text-brand-gold text-sm text-center">
                No admin account found. 
                <button 
                  onClick={() => setMode('setup')} 
                  className="underline ml-1 hover:text-brand-gold-light"
                >
                  Create one
                </button>
              </p>
            </div>
          )}

          <form onSubmit={mode === 'setup' ? handleSetup : handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white/70">Username</Label>
              <Input
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className="bg-brand-charcoal border-white/10 text-white focus:border-brand-red"
                placeholder="Enter username"
                data-testid="login-username"
              />
            </div>

            {mode === 'setup' && (
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70">Email (optional)</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-brand-charcoal border-white/10 text-white focus:border-brand-red"
                  placeholder="admin@example.com"
                  data-testid="login-email"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={mode === 'setup' ? 6 : undefined}
                className="bg-brand-charcoal border-white/10 text-white focus:border-brand-red"
                placeholder="Enter password"
                data-testid="login-password"
              />
              {mode === 'setup' && (
                <p className="text-white/30 text-xs">Minimum 6 characters</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : mode === 'setup' ? (
                <span className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Create Account
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-4 h-4" />
                  Sign In
                </span>
              )}
            </Button>
          </form>

          {mode === 'setup' && !needsSetup && (
            <p className="text-center mt-4">
              <button 
                onClick={() => setMode('login')} 
                className="text-white/50 hover:text-brand-red text-sm"
              >
                Back to login
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
